import { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  FormModal,
  FormStack,
  Input,
  Modal,
  Page,
  Text,
} from "@marekl/adminer/components";
import { useConfirm, useT } from "@marekl/adminer/hooks";
import { apiErrorMessage, notifyError } from "@marekl/adminer/api";
import { MusicPanel } from "../../../components/MusicPanel";
import { formatDuration, formatFileSize } from "../../../lib/musicState";
import {
  recordingFileUrl,
  useCreateRecording,
  useReplaceRecordingFile,
  type EntryMusic,
} from "../../../lib/queries/music";
import {
  useDeleteRecording,
  useRenameRecording,
  type Recording,
} from "../../../lib/queries/rules";
import { RuleChecks } from "../../../components/RuleChecks";
import {
  recordingQualityIssues,
  summarizeRecordingQuality,
} from "../../../lib/rulesChecks";
import { ComponentCard } from "./ComponentCard";
import { reorderRows } from "./reorder";
import tableStyles from "./componentTables.module.css";
import type { ID } from "../../../lib/id";

type ModalState =
  | { kind: "closed" }
  | { kind: "new" }
  | { kind: "edit"; recording: Recording };

/**
 * A Recording carries the same measurements as an entry's upload but no
 * verdicts: it is danced to by every combination that resolves to it,
 * and those may allow different lengths — TD Twirl's tým combinations
 * permit 2:50 where its duo/trio ones permit 1:58 — so there is no
 * single limit to judge it against.
 */
const asPanelMusic = (r: Recording): EntryMusic => ({
  ...r,
  uploaded_at: r.updated_at,
  warnings: [],
});

/**
 * The official ČNSMS recordings of one rule set.
 *
 * A flat list, like the floors: rows rather than a file bolted onto each
 * discipline, because one discipline can need several. TD Twirl is the
 * case that forced it — duo/trio and tým are danced to different tracks,
 * exactly as they already have different time limits — and while a
 * recording hung off the discipline there was room for one, so half the
 * twirl field would have been handed the other half's music.
 *
 * A discipline picks its default in „Upravit disciplínu"; a combination
 * that departs from it says so in the matrix.
 */
export function RecordingsTab({
  ruleSetId,
  readOnly,
  recordings,
  minBitrateKbps,
  minSampleRateHz,
}: {
  ruleSetId: ID;
  readOnly: boolean;
  recordings: Recording[];
  /** The set's own quality floors, from „Nastavení". Zero means none. */
  minBitrateKbps: number;
  minSampleRateHz: number;
}) {
  // Whether the federation's own tracks clear the floor the federation
  // set. Only ever shown here: the club sees no verdict on a ČNSMS track,
  // because it cannot replace one.
  const qualityIssues = recordingQualityIssues(recordings, minBitrateKbps, minSampleRateHz);
  const belowFloor = new Set(qualityIssues.map((i) => i.recording.id));
  const confirm = useConfirm();
  const t = useT();
  const [modal, setModal] = useState<ModalState>({ kind: "closed" });
  const [name, setName] = useState("");

  const create = useCreateRecording(ruleSetId, name.trim());
  const rename = useRenameRecording();
  const remove = useDeleteRecording();

  const close = () => {
    setModal({ kind: "closed" });
    create.reset();
    rename.reset();
  };

  const open = (recording?: Recording) => {
    setName(recording?.name ?? "");
    create.reset();
    rename.reset();
    setModal(recording ? { kind: "edit", recording } : { kind: "new" });
  };

  const submitName = () => {
    if (modal.kind !== "edit") return;
    rename.mutate(
      {
        id: modal.recording.id,
        payload: { name: name.trim(), sort_order: modal.recording.sort_order },
      },
      { onSuccess: close },
    );
  };

  const move = (index: number, direction: -1 | 1) => {
    for (const { row, sort_order } of reorderRows(recordings, index, direction)) {
      rename.mutate(
        { id: row.id, payload: { name: row.name, sort_order } },
        { onError: (err) => notifyError(err) },
      );
    }
  };

  return (
    <>
      <Page.SectionHeader
        title="Hudba"
        meta={
          readOnly ? undefined : (
            <Button variant="primary" onClick={() => open()}>
              Přidat nahrávku
            </Button>
          )
        }
      />
      <Text variant="muted">
        Nahrávky, na které se soutěží v disciplínách s hudbou ČNSMS. Disciplína si jednu
        vybere jako výchozí a kombinace si může vybrat jinou. Týmy si je mohou přehrát
        i stáhnout na trénink; do exportu pro zvukaře se vloží ke každému startovnímu
        číslu.
      </Text>

      {/* The federation's own tracks measured against the federation's
          own floor. It lives here and nowhere else: a club cannot
          replace a ČNSMS recording, so telling it the track is poor is a
          message it can only forward. */}
      <RuleChecks warnings={summarizeRecordingQuality(qualityIssues)} />

      <ComponentCard<Recording>
        title="Oficiální nahrávky"
        subtitle={<Text variant="muted">{recordings.length}</Text>}
        readOnly={readOnly}
        rows={recordings}
        emptyTitle="Žádné nahrávky"
        emptyDescription="Přidejte nahrávku, na kterou se soutěží, například Twirl duo/trio."
        addLabel="Přidat nahrávku"
        columns={[
          {
            key: "name",
            header: "Název",
            tdClass: tableStyles.nameOnly,
            render: (r) => (
              <>
                <div>{r.name}</div>
                <Text variant="muted">{r.original_name}</Text>
              </>
            ),
          },
          {
            key: "duration",
            header: "Délka",
            numeric: true,
            tdClass: tableStyles.value,
            render: (r) => <Text variant="mono">{formatDuration(r.duration_ms)}</Text>,
          },
          {
            key: "quality",
            header: "Kvalita",
            numeric: true,
            tdClass: tableStyles.value,
            // The measurement, and a marker when it is under the set's
            // own floor. `warn`, never `err`: the track plays, the
            // competition runs, and this is advice about a master worth
            // re-exporting.
            render: (r) => (
              <>
                <Text variant="mono">{r.bitrate_kbps} kbps</Text>
                {belowFloor.has(r.id) && (
                  <>
                    {" "}
                    <Badge variant="warn">Špatná kvalita nahrávky</Badge>
                  </>
                )}
              </>
            ),
          },
          {
            key: "size",
            header: "Velikost",
            numeric: true,
            tdClass: tableStyles.value,
            render: (r) => <Text variant="mono">{formatFileSize(r.size_bytes)}</Text>,
          },
        ]}
        onAddRow={() => open()}
        onEditRow={(recording) => open(recording)}
        onDeleteRow={(recording) =>
          confirm({
            title: "Smazat nahrávku",
            description: (
              <>
                Opravdu smazat nahrávku <strong>„{recording.name}“</strong>? Nahrávku, kterou
                používá disciplína nebo kombinace, smazat nelze.
              </>
            ),
            confirmLabel: "Smazat",
            variant: "danger",
            onConfirm: () => remove.mutateAsync(recording.id),
          })
        }
        onMoveRow={move}
      />

      <Modal
        open={modal.kind === "new"}
        title="Nová nahrávka"
        onClose={close}
        footer={
          <Button onClick={close}>
            Zavřít
          </Button>
        }
      >
        {create.error && <Alert variant="err">{apiErrorMessage(create.error, t)}</Alert>}
        <FormStack>
          <Input
            label="Název"
            required
            placeholder="Twirl duo/trio"
            value={name}
            onChange={(e) => setName(e.target.value)}
            hint="Pod tímhle názvem si ji vyberou disciplíny a kombinace."
          />
          {/* The upload IS the save: naming the recording and choosing
              its file are one request, so there is no „Uložit" to press
              afterwards. Until the name is filled there is nothing to
              save it under. */}
          <MusicPanel
            src=""
            music={null}
            disabled={name.trim() === ""}
            disabledReason="Nejdřív vyplňte název."
            audience="organizer"
            upload={{
              mutateAsync: (file) => create.mutateAsync(file).then(asPanelMusic),
              isPending: create.isPending,
              error: create.error,
            }}
            onUploaded={close}
          />
        </FormStack>
      </Modal>

      {modal.kind === "edit" && (
        <EditRecordingModal
          recording={modal.recording}
          name={name}
          setName={setName}
          onRename={submitName}
          renaming={rename.isPending}
          renameError={rename.error}
          onClose={close}
        />
      )}

    </>
  );
}

/**
 * Its own component because useReplaceRecordingFile is keyed to the
 * recording and hooks cannot be called conditionally.
 */
function EditRecordingModal({
  recording,
  name,
  setName,
  onRename,
  renaming,
  renameError,
  onClose,
}: {
  recording: Recording;
  name: string;
  setName: (v: string) => void;
  onRename: () => void;
  renaming: boolean;
  renameError: unknown;
  onClose: () => void;
}) {
  const replace = useReplaceRecordingFile(recording.id);
  return (
    <FormModal
      open
      title="Upravit nahrávku"
      onClose={onClose}
      submitLabel="Uložit"
      busy={renaming}
      canSubmit={name.trim() !== ""}
      error={renameError}
      onSubmit={onRename}
    >
      <Input
        label="Název"
        required
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      {/* Replacing the file is its own request and takes effect at
          once — it keeps every reference to this recording, which is
          the point of replacing rather than deleting and re-adding. */}
      <MusicPanel
        src={recordingFileUrl(recording.id)}
        music={asPanelMusic(recording)}
        audience="organizer"
        upload={{
          mutateAsync: (file) => replace.mutateAsync(file).then(asPanelMusic),
          isPending: replace.isPending,
          error: replace.error,
        }}
      />
    </FormModal>
  );
}
